import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  UpdateProfileRequest,
  UpdateProfileResponse,
  updateProfileContract,
  ChangePasswordRequest,
  ChangePasswordResponse,
  changePasswordContract,
  ErrorCode
} from '@bliks-io/contracts';
import { apiClient } from '../client';
import type { ApiError } from '@/lib/agent.types';
import { invalidateEntity } from '../cache-invalidation';
import { toast } from 'sonner';
import { toastApiError, toastRequestError } from './toast-api-error';
import i18next from '@/lib/i18n';

/**
 * Update Profile mutation
 *
 * Allows users to update their own profile information.
 * - Updates firstName, lastName, and language
 * - Invalidates profile cache to refetch updated data
 *
 * @example
 * ```tsx
 * function ProfileTab() {
 *   const updateProfile = useUpdateProfile();
 *
 *   const handleSubmit = async (data: UpdateProfileRequest) => {
 *     try {
 *       await updateProfile.mutateAsync(data);
 *       toast.success('Profile updated successfully');
 *     } catch (error) {
 *       // The hook's onError already showed the error toast.
 *     }
 *   };
 *
 *   return (
 *     <form onSubmit={handleSubmit}>
 *       <Button loading={updateProfile.isPending}>Save</Button>
 *     </form>
 *   );
 * }
 * ```
 */
export function useUpdateProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: UpdateProfileRequest): Promise<UpdateProfileResponse> => {
      const { data } = await apiClient.patch<UpdateProfileResponse>(
        updateProfileContract.path,
        request
      );
      return data;
    },
    onSuccess: async (_data, variables) => {
      invalidateEntity(queryClient, 'user');
      // Switch language before the toast so it renders in the new language.
      if (variables.language && variables.language !== i18next.language) {
        await i18next.changeLanguage(variables.language);
      }
      toast.success(i18next.t('userSettings.profile.updateSuccess'));
    },
    onError: (error) => {
      toastApiError(error, 'userSettings.profile.updateError');
    }
  });
}

/**
 * Change Password mutation
 *
 * Allows users to change their password.
 * - Requires current password for verification
 * - New password must meet security requirements
 *
 * @example
 * ```tsx
 * function SecurityTab() {
 *   const changePassword = useChangePassword();
 *
 *   const handleSubmit = async (data: ChangePasswordRequest) => {
 *     try {
 *       await changePassword.mutateAsync(data);
 *       toast.success('Password changed successfully');
 *       reset(); // Clear form
 *     } catch (error) {
 *       // The hook's onError already showed the error toast.
 *     }
 *   };
 *
 *   return (
 *     <form onSubmit={handleSubmit}>
 *       <Button loading={changePassword.isPending}>Change Password</Button>
 *     </form>
 *   );
 * }
 * ```
 */
export function useChangePassword() {
  return useMutation({
    mutationFn: async (request: ChangePasswordRequest): Promise<ChangePasswordResponse> => {
      const { data } = await apiClient.post<ChangePasswordResponse>(
        changePasswordContract.path,
        request
      );
      return data;
    },
    onSuccess: () => {
      toast.success(i18next.t('userSettings.security.passwordSuccess'));
    },
    onError: (error: unknown) => {
      toastRequestError(error, i18next.t(resolvePasswordChangeErrorKey(error)));
    }
  });
}

/**
 * Maps a failed password change to its toast translation key. The v2 agent
 * (src/lib/agent.ts) rejects every apiClient failure as a flat ApiError, so the
 * backend's i18n code and status arrive as top-level `message`/`statusCode`.
 * Prefer the code — not every 400 is a wrong current password (e.g. ssoRequired,
 * passwordUnchanged).
 */
export function resolvePasswordChangeErrorKey(error: unknown): string {
  const { message, statusCode } = (error ?? {}) as Partial<ApiError>;
  const errorCode =
    typeof message === 'string' && message.startsWith('errors.') ? message : undefined;

  // A wrong current password keeps the change-form-specific copy rather than
  // the login-oriented invalidPassword string.
  if (errorCode === ErrorCode.AUTH_INVALID_PASSWORD) {
    return 'userSettings.security.invalidCurrentPassword';
  }
  if (errorCode && i18next.exists(errorCode)) {
    return errorCode;
  }
  if (statusCode === 401 || statusCode === 400) {
    return 'userSettings.security.invalidCurrentPassword';
  }
  return 'userSettings.security.passwordError';
}
